// React hooks over pendingPhotoStore + pendingMutationStore. Subscribes
// to both IDB pub/sub channels so the grid (and markup/tag/issue hooks)
// re-render when uploads complete or when the user queues an offline
// markup/tag/status change against a still-pending photo.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import * as photoStore from '../lib/pendingPhotoStore'
import type { PendingPhoto } from '../lib/pendingPhotoStore'
import * as mutationStore from '../lib/pendingMutationStore'
import type { PendingMutations } from '../lib/pendingMutationStore'
import type { PhotoWithTags, Tag } from '../types/database'

// ── photos snapshot ──────────────────────────────────────────────────────────
// One cached snapshot module-wide so multiple components don't each
// hit IDB. Refreshed whenever the store emits a change.

let cachedPhotos: PendingPhoto[] = []
let photosGeneration = 0
let photosInitialized = false

const photoSubscribers = new Set<() => void>()

async function refreshPhotos() {
  cachedPhotos = await photoStore.getAll()
  photosGeneration++
  for (const fn of photoSubscribers) fn()
}

// Force-refresh the cached pending-photos snapshot and resolve once
// the cache contains `awaitLocalId` (or once we've completed a refresh
// pass, whichever comes first). Use this before navigating to a route
// that reads a freshly-enqueued photo from the cache — otherwise the
// route mounts on a stale snapshot (because notify() kicks refresh
// asynchronously) and may briefly think the photo is server-side.
export async function refreshPendingPhotosNow(awaitLocalId?: string): Promise<void> {
  ensurePhotosInitialized()
  await refreshPhotos()
  if (awaitLocalId && !cachedPhotos.some(p => p.local_id === awaitLocalId)) {
    // One more pass in case there was a race with another writer.
    await refreshPhotos()
  }
}

function ensurePhotosInitialized() {
  if (photosInitialized) return
  photosInitialized = true
  photoStore.subscribe(() => { void refreshPhotos() })
  void refreshPhotos()
}

function subscribePhotosReact(fn: () => void): () => void {
  ensurePhotosInitialized()
  photoSubscribers.add(fn)
  return () => { photoSubscribers.delete(fn) }
}

function getPhotosSnapshot(): number {
  return photosGeneration
}

// ── mutations snapshot ───────────────────────────────────────────────────────

let cachedMutations: Map<string, PendingMutations> = new Map()
let mutationsGeneration = 0
let mutationsInitialized = false

const mutationSubscribers = new Set<() => void>()

async function refreshMutations() {
  const all = await mutationStore.getAll()
  cachedMutations = new Map(all.map(m => [m.local_id, m]))
  mutationsGeneration++
  for (const fn of mutationSubscribers) fn()
}

function ensureMutationsInitialized() {
  if (mutationsInitialized) return
  mutationsInitialized = true
  mutationStore.subscribe(() => { void refreshMutations() })
  void refreshMutations()
}

function subscribeMutationsReact(fn: () => void): () => void {
  ensureMutationsInitialized()
  mutationSubscribers.add(fn)
  return () => { mutationSubscribers.delete(fn) }
}

function getMutationsSnapshot(): number {
  return mutationsGeneration
}

// ── public hooks ─────────────────────────────────────────────────────────────

// All pending photos (any project), FIFO ordered.
export function useAllPendingPhotos(): PendingPhoto[] {
  useSyncExternalStore(subscribePhotosReact, getPhotosSnapshot, getPhotosSnapshot)
  return cachedPhotos
}

export function usePendingPhotos(projectId: string | undefined): PendingPhoto[] {
  const all = useAllPendingPhotos()
  return projectId ? all.filter(p => p.project_id === projectId) : []
}

export function usePendingCount(): number {
  return useAllPendingPhotos().length
}

// Synchronous-feeling check whether a photo id is currently in the
// pending queue. Hooks that need to branch between "queue mutation
// locally" vs "hit Supabase" use this.
export function useIsPending(photoId: string | undefined): boolean {
  const all = useAllPendingPhotos()
  return photoId ? all.some(p => p.local_id === photoId) : false
}

// Full map of queued mutations, keyed by photo local_id. Updates on
// every change to the mutation store.
export function useAllPendingMutations(): Map<string, PendingMutations> {
  useSyncExternalStore(subscribeMutationsReact, getMutationsSnapshot, getMutationsSnapshot)
  return cachedMutations
}

export function usePendingMutations(photoId: string | undefined): PendingMutations | null {
  const all = useAllPendingMutations()
  return photoId ? all.get(photoId) ?? null : null
}

// Convert a pending entry to a PhotoWithTags-shaped object so it can
// flow through the existing grid/lightbox path. Queued mutations
// (tags/issue_status/markup) are applied here so the grid renders the
// right badges/chips even though the photo hasn't been uploaded yet.
// The blob URL is stuffed into web_path / thumbnail_path / etc. so
// getPhotoUrl-style callers resolve to the local image directly.
// __pending is set so the grid knows to render the "uploading" overlay
// and ignore taps for non-markup actions.
export function pendingToPhotoWithTags(
  p: PendingPhoto,
  blobUrl: string,
  mutations: PendingMutations | null,
  allTags: Tag[],
): PhotoWithTags {
  const tagIds = mutations?.tag_ids ?? []
  // Preserve tag order from allTags (sort_order) for consistent badge rendering.
  const tags = allTags.filter(t => tagIds.includes(t.id))
  const markupData = mutations?.markup_data ?? null
  return {
    id: p.local_id,
    project_id: p.project_id,
    user_id: p.user_id,
    photographer_name: p.photographer_name,
    original_path: blobUrl,
    web_path: blobUrl,
    thumbnail_path: blobUrl,
    small_thumbnail_path: blobUrl,
    marked_up_path: null,
    latitude: p.latitude,
    longitude: p.longitude,
    taken_at: p.captured_at,
    uploaded_at: p.captured_at,        // placeholder; the row hasn't really been uploaded yet
    deleted_at: null,
    issue_status: mutations?.issue_status ?? 'not_an_issue',
    tags,
    markup: markupData
      ? { id: `pending-${p.local_id}`, photo_id: p.local_id, markup_data: markupData, created_at: p.captured_at }
      : null,
    user_name: p.photographer_name,
    __pending: { status: p.status, attempts: p.attempts },
  }
}

// Manages object URLs for a set of pending photos. New photos get a
// URL on next render; departed photos have their URL revoked. All URLs
// are revoked on unmount.
export function useBlobUrls(photos: PendingPhoto[]): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(() => new Map())
  const lastIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    const currentIds = new Set(photos.map(p => p.local_id))
    let changed = false
    const next = new Map(urls)

    for (const p of photos) {
      if (!next.has(p.local_id)) {
        next.set(p.local_id, URL.createObjectURL(p.original_blob))
        changed = true
      }
    }
    for (const id of lastIds.current) {
      if (!currentIds.has(id)) {
        const url = next.get(id)
        if (url) URL.revokeObjectURL(url)
        next.delete(id)
        changed = true
      }
    }
    lastIds.current = currentIds
    if (changed) setUrls(next)
  }, [photos, urls])

  useEffect(() => {
    return () => {
      for (const url of lastIds.current) {
        const u = urls.get(url)
        if (u) URL.revokeObjectURL(u)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return urls
}
